import React, { createContext, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, AlertCircle, Info, AlertTriangle, X, Bell,
  DollarSign, Star, Car, Gift
} from 'lucide-react';

// Notification Context
const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// Notification Provider
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Add notification
  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      timestamp: new Date(),
      read: false,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev]);

    // Play sound if enabled
    if (soundEnabled && notification.sound !== false) {
      playNotificationSound(notification.type);
    }

    // Auto remove after duration
    if (notification.duration !== 0) {
      setTimeout(() => {
        removeNotification(id);
      }, notification.duration || 5000);
    }

    return id;
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Mark as read
  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // Clear all
  const clearAll = () => {
    setNotifications([]);
  };

  // Play sound
  const playNotificationSound = (type) => {
    try {
      const audio = new Audio();
      switch (type) {
        case 'ride':
          audio.src = '/sounds/ride-request.mp3';
          break;
        case 'success':
          audio.src = '/sounds/success.mp3';
          break;
        case 'warning':
          audio.src = '/sounds/warning.mp3';
          break;
        case 'error':
          audio.src = '/sounds/error.mp3';
          break;
        default:
          audio.src = '/sounds/notification.mp3';
      }
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  };

  // Predefined notification types
  const notify = {
    success: (message, options = {}) =>
      addNotification({ type: 'success', message, icon: CheckCircle, ...options }),

    error: (message, options = {}) =>
      addNotification({ type: 'error', message, icon: AlertCircle, ...options }),

    warning: (message, options = {}) =>
      addNotification({ type: 'warning', message, icon: AlertTriangle, ...options }),

    info: (message, options = {}) =>
      addNotification({ type: 'info', message, icon: Info, ...options }),

    ride: (message, options = {}) =>
      addNotification({ type: 'ride', message, icon: Car, sound: true, ...options }),

    earnings: (message, options = {}) =>
      addNotification({ type: 'earnings', message, icon: DollarSign, ...options }),

    rating: (message, options = {}) =>
      addNotification({ type: 'rating', message, icon: Star, ...options }),

    bonus: (message, options = {}) =>
      addNotification({ type: 'bonus', message, icon: Gift, ...options }),

    custom: (notification) =>
      addNotification(notification)
  };

  const value = {
    notifications,
    unreadCount: notifications.filter(n => !n.read).length,
    addNotification,
    removeNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
    soundEnabled,
    setSoundEnabled,
    notify
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationToasts />
    </NotificationContext.Provider>
  );
};

// Notification Toasts Component
const NotificationToasts = () => {
  const { notifications, removeNotification } = useNotifications();
  const recentNotifications = notifications.slice(0, 3);

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-2 max-w-md">
      <AnimatePresence>
        {recentNotifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Single Notification Toast
const NotificationToast = ({ notification, onClose }) => {
  const Icon = notification.icon || Bell;

  const typeStyles = {
    success: 'bg-green-500/20 border-green-500/50 text-green-500',
    error: 'bg-red-500/20 border-red-500/50 text-red-500',
    warning: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-500',
    info: 'bg-blue-500/20 border-blue-500/50 text-blue-500',
    ride: 'bg-blue-500/20 border-blue-500/50 text-blue-500',
    earnings: 'bg-green-500/20 border-green-500/50 text-green-500',
    rating: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-500',
    bonus: 'bg-purple-500/20 border-purple-500/50 text-purple-500'
  };

  const bgStyles = {
    success: 'from-green-500/10 to-green-600/5',
    error: 'from-red-500/10 to-red-600/5',
    warning: 'from-yellow-500/10 to-yellow-600/5',
    info: 'from-blue-500/10 to-blue-600/5',
    ride: 'from-blue-500/10 to-blue-600/5',
    earnings: 'from-green-500/10 to-green-600/5',
    rating: 'from-yellow-500/10 to-yellow-600/5',
    bonus: 'from-purple-500/10 to-purple-600/5'
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 100, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.9 }}
      className={`bg-gradient-to-r ${bgStyles[notification.type] || bgStyles.info} backdrop-blur-xl border rounded-xl shadow-2xl overflow-hidden`}
    >
      <div className="flex items-start gap-3 p-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeStyles[notification.type] || typeStyles.info}`}>
          <Icon size={20} />
        </div>

        <div className="flex-1 min-w-0">
          {notification.title && (
            <h4 className="text-white font-bold text-sm mb-1">{notification.title}</h4>
          )}
          <p className="text-gray-300 text-sm">{notification.message}</p>
          {notification.subtitle && (
            <p className="text-gray-400 text-xs mt-1">{notification.subtitle}</p>
          )}
          {notification.action && (
            <button
              onClick={notification.action.onClick}
              className="mt-2 text-xs font-bold text-blue-500 hover:text-blue-400 transition-colors"
            >
              {notification.action.label}
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          className="p-1 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
        >
          <X size={16} className="text-gray-400" />
        </button>
      </div>

      {/* Progress bar */}
      {notification.duration && notification.duration > 0 && (
        <motion.div
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: notification.duration / 1000, ease: 'linear' }}
          className={`h-1 ${typeStyles[notification.type]?.replace('text-', 'bg-')}`}
        />
      )}
    </motion.div>
  );
};

// Notification Badge Component
export const NotificationBadge = ({ count, className = '' }) => {
  if (!count || count === 0) return null;

  return (
    <motion.span
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      className={`absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center shadow-lg ${className}`}
    >
      {count > 99 ? '99+' : count}
    </motion.span>
  );
};

export default NotificationProvider;
